/**
 * Referentially-stable building blocks for the `EntityHooks<T>` contract.
 *
 * Every `EntityHooks` member is a hook whose RETURN VALUE is consumed as a
 * dependency further up: `useBuildActionContext` lists `hooks`,
 * `updateMutation` and `deleteMutation` in its `useMemo` deps, and
 * `useTableRowActionContext` does the same for the row-action path. A hook that
 * returns a fresh object literal every render therefore invalidates that memo
 * on EVERY render, which re-renders `UniversalDetailTabs` /
 * `UniversalDetailTabContent` no matter how carefully the consumers are
 * memoized. See MEMO_STABLE_PROPS.
 *
 * Two shapes recur across the entity hooks, so they live here once:
 *
 *  - the constant "this entity has no infinite / paginated query" results,
 *    which are allocated ONCE at module scope rather than per render;
 *  - `useStableMutationResult`, which memoizes `{ mutateAsync, isPending }`.
 *
 * NOTE on `useStableMutationResult` deps: `mutateAsync` must ALREADY be stable
 * (a `useCallback`, or a bound method a client hands back). Apollo v4's
 * `useMutation` execute function is `useCallback(…, [])` reading everything off
 * a ref, and TanStack Query v5's `mutate`/`mutateAsync` are bound once in the
 * observer — both qualify. Passing a fresh closure is not WRONG (the memo just
 * recomputes, i.e. today's behaviour); it simply buys nothing.
 */
import { useMemo } from "react";

const noop = (): void => {};

/** Shape of the `EntityHooks<T>["useInfinite"]` return with no data. */
interface EmptyInfiniteResult {
  data: undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/** Shape of the `EntityHooks<T>["usePaginated"]` return with no data. */
interface EmptyPaginatedResult {
  data: undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * The single allocation returned by every `useInfinite` that is not implemented.
 * Assignable to `EntityHooks<T>["useInfinite"]`'s return for any `T` because
 * `data` is `undefined`.
 */
export const EMPTY_INFINITE_RESULT: EmptyInfiniteResult = {
  data: undefined,
  isLoading: false,
  error: null,
  refetch: noop,
  hasNextPage: false,
  isFetchingNextPage: false,
  fetchNextPage: noop,
};

/** The single allocation returned by every `usePaginated` that is not implemented. */
export const EMPTY_PAGINATED_RESULT: EmptyPaginatedResult = {
  data: undefined,
  isLoading: false,
  error: null,
  refetch: noop,
};

/**
 * Memoize an entity mutation hook's `{ mutateAsync, isPending }` result so the
 * object identity only changes when the pending flag flips (or the callback
 * itself does). `isPending` is a primitive, so this is a value comparison —
 * there is no way for it to serve a stale `mutateAsync`.
 */
export function useStableMutationResult<TMutateAsync>(
  mutateAsync: TMutateAsync,
  isPending: boolean
): { mutateAsync: TMutateAsync; isPending: boolean } {
  return useMemo(() => ({ mutateAsync, isPending }), [mutateAsync, isPending]);
}
